class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // Use event delegation so overlay click works after renderContents replaces the overlay
    this.addEventListener('click', (evt) => {
      if (evt.target.id === 'CartDrawer-Overlay') this.close();
    });
    this.setHeaderCartIconAccessibility();
    this.addEventListener('click', this.handleUpsellAdd.bind(this));
    // Safety: restore body scroll if drawer is closed but overflow-hidden was left stuck
    document.addEventListener('visibilitychange', this._restoreBodyScrollIfStuck.bind(this));
  }

  _restoreBodyScrollIfStuck() {
    if (document.visibilityState !== 'visible') return;
    const cartOpen = this.classList.contains('active');
    const bodyLocked = document.body.classList.contains('cart-drawer-open');
    if (!cartOpen && bodyLocked) {
      document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
      document.documentElement.classList.remove('cart-drawer-open');
    }
  }

  handleUpsellAdd(evt) {
    const btn = evt.target.closest('[data-cart-upsell-add]');
    if (!btn) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    evt.preventDefault();
    btn.disabled = true;
    btn.classList.add('loading');
    const spinner = btn.querySelector('.loading__spinner');
    const btnText = btn.querySelector('.cart-drawer__upsell-add-text');
    if (spinner) spinner.classList.remove('hidden');
    if (btnText) btnText.classList.add('hidden');

    const config = typeof fetchConfig !== 'undefined' ? fetchConfig('javascript') : { method: 'POST' };
    config.headers = config.headers ? { ...config.headers } : {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    formData.append('sections', this.getSectionsToRender().map((s) => s.id));
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    this.setActiveElement(btn);

    const cartAddUrl = (typeof routes !== 'undefined' ? routes : window?.routes)?.cart_add_url || '/cart/add.js';

    fetch(cartAddUrl, config)
      .then((res) => res.json())
      .then((response) => {
        if (response.status) {
          if (typeof publish === 'function' && PUB_SUB_EVENTS?.cartError) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'cart-drawer-upsell',
              productVariantId: variantId,
              errors: response.errors || response.description,
              message: response.message,
            });
          }
          throw new Error(response.description || 'Error adding to cart');
        }

        if (typeof publish === 'function' && PUB_SUB_EVENTS?.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer-upsell',
            productVariantId: variantId,
            cartData: response,
          });
        }

        if (this.renderContents && response.sections) {
          this.renderContents(response);
        }
      })
      .catch((err) => {
        console.error('Cart upsell add failed:', err);
        alert(err.message || 'Error adding to cart. Please try again.');
      })
      .finally(() => {
        btn.disabled = false;
        btn.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
        if (btnText) btnText.classList.remove('hidden');
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden', 'cart-drawer-open');
    document.documentElement.classList.add('cart-drawer-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
    document.documentElement.classList.remove('cart-drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
